import React, { useState } from 'react';
import { Box, IconButton, LinearProgress, Stack } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';

import { Rows } from './Rows';
import { TableCell } from './TableCell';
import { TablePagination } from './TablePagination';
import { Column, Group, ListingEvent, StaticSettings } from '../types';

interface Props<Data extends Record<string, any>, Context extends any = any> {
  context: Context;
  groups: Group<Data>[];
  staticSettings?: StaticSettings;
  columns: Column<Data, Context>[];
  onEmit?: (event: ListingEvent, data?: unknown) => void;
}

export const RowsGroup = <Data extends Record<string, any>, Context extends any = any>({
  onEmit,
  groups,
  context,
  columns,
  staticSettings,
}: Props<Data, Context>) => {
  const [openGroups, setIsOpen] = useState<Record<string, boolean>>({});

  return (
    <Box position="relative">
      {groups.map(group => {
        const isOpen = openGroups[group.name] ?? staticSettings?.allGroupsOpen ?? true;

        return (
          <Box key={group.name}>
            {group?.isLoading && <LinearProgress color="primary" className="listing-loading" />}

            {group?.beforeGroup && (
              <TableCell context={context} sx={{ ...(group.beforeGroup?.cellStyles ?? {}) }}>
                <Stack direction="row" gap="0.2rem" alignItems="center">
                  <IconButton
                    onClick={() =>
                      setIsOpen(prev => {
                        const nextValue = prev[group.name] === undefined ? false : !prev[group.name];

                        onEmit?.(nextValue ? 'groupOpened' : 'groupClosed', group.name);

                        return { ...prev, [group.name]: nextValue };
                      })
                    }
                  >
                    {isOpen ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                  </IconButton>

                  {group.beforeGroup.render(group)}
                </Stack>
              </TableCell>
            )}

            {isOpen && (
              <>
                <Rows tableData={group.data} context={context} columns={columns} />

                {group.pagination && <TablePagination pagination={group.pagination} />}

                {group?.afterGroup && (
                  <TableCell context={context} sx={{ ...(group.afterGroup?.cellStyles ?? {}) }}>
                    {group.afterGroup.render(group)}
                  </TableCell>
                )}
              </>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
